"use client";

import { useTranslations } from "next-intl";
import React from "react";

export type DropdownProps = {
  name: string;
  label?: string;
  ariaLabel?: string;
  options: string[] | { label: string; value: string }[];
  defaultValue?: string;
  placeholder?: string;
  onChange?: (item: string, type: string) => void;
  disabled?: boolean;
  className?: string;
};

export default function Dropdown({ name, label, ariaLabel, options, defaultValue, placeholder, onChange, disabled, className }: DropdownProps) {
  const t = useTranslations();

  const normalizedOptions = React.useMemo(() => {
    if (typeof options[0] === "string") {
      return (options as string[]).map((item) => ({
        label: name === "price" ? item : t(`header.measurement.${item}`),
        value: item,
      }));
    }
    return options as { label: string; value: string }[];
  }, [options]);

  const initialValue = defaultValue ?? (placeholder ? "" : normalizedOptions[0]?.value ?? "");
  const [selected, setSelected] = React.useState(initialValue);

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const nextValue = e.target.value;
    setSelected(nextValue);
    onChange?.(nextValue, name);
  };

  return (
    <div className={className}>
      {label && <label htmlFor={label}>{label}</label>}

      <select
        id={label}
        name={name}
        value={selected}
        onChange={handleChange}
        disabled={disabled}
        aria-label={label ? undefined : ariaLabel ?? "Dropdown"}
        className={"block border-b-2 border-gray-300 bg-white px-3 py-2 text-s shadow-sm outline-none disabled:bg-gray-100"}
      >
        {normalizedOptions.map((opt) => (
          <option key={opt.value} value={opt.value} disabled={selected === opt.value} className={selected === opt.value ? "bg-gray-30" : ""}>
            {opt.label}
          </option>
        ))}
      </select>
    </div>
  );
}
